import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ComponentRef,
  computed,
  effect,
  EnvironmentInjector,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnDestroy,
  Output,
  signal,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthDataService } from '@nomoh-monorepo/data-access-auth';
import { PessoaDataService } from '@nomoh-monorepo/data-access-shared-services';
import { IFeatureSHeader } from '@nomoh-monorepo/shared-angular-type-const';
import { HelpCenterService, LanguageSelectorComponent, NPopoverComponent } from '@nomoh-monorepo/shared-common-ui';
import { ThemeService } from '@nomoh-monorepo/shared-theme';
import { IPessoa } from '@nomoh-monorepo/shared-types';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { TagModule } from 'primeng/tag';
import { Subscription } from 'rxjs';
import { FeatureSPwaOnlineStatusComponent } from '../feature-s-pwa-online-status/feature-s-pwa-online-status.component';
import { FEATURE_S_HEADER_CALENDAR_COMPONENT } from './header-calendar.token';
import { FEATURE_S_HEADER_HELP } from './header-help.token';
import { FEATURE_S_HEADER_PERFIL_LABEL } from './header-perfil-label.token';
import { FEATURE_S_HEADER_TOUR_REPLAY } from './header-tour-replay.token';

@Component({
  selector: 'lib-feature-s-header',
  standalone: true,
  imports: [
    BadgeModule,
    AvatarModule,
    CommonModule,
    ButtonModule,
    FeatureSPwaOnlineStatusComponent,
    BadgeModule,
    OverlayBadgeModule,
    NPopoverComponent,
    LanguageSelectorComponent,
    RouterLink,
    TagModule,
    TranslateModule,
  ],
  templateUrl: './feature-s-header.component.html',
  styleUrls: ['./feature-s-header.component.scss'],
})
export class FeatureSHeaderComponent implements AfterViewInit, OnDestroy {
  //#region ingjeção de dependencia
  helpCenterService = inject(HelpCenterService);
  themeService = inject(ThemeService);
  autDataService = inject(AuthDataService);
  pessoaDataService = inject(PessoaDataService);
  router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly calendarPopoverConfig = inject(
    FEATURE_S_HEADER_CALENDAR_COMPONENT,
    { optional: true }
  );
  private readonly perfilLabelSignal = inject(FEATURE_S_HEADER_PERFIL_LABEL, {
    optional: true,
  });
  private readonly tourReplay = inject(FEATURE_S_HEADER_TOUR_REPLAY, {
    optional: true,
  });
  private readonly helpEnabled = inject(FEATURE_S_HEADER_HELP, {
    optional: true,
  });
  //#endregion

  //#region variáveis
  isHeaderHidden = false;
  private calendarPopoverComponentRef?: ComponentRef<unknown>;
  private readonly calendarPopoverComponentType =
    this.calendarPopoverConfig?.component ?? null;
  private calendarItemClickedSub?: Subscription;
  private readonly calendarBadgeSignal =
    this.calendarPopoverConfig?.badgeCount ?? signal(0);
  //#endregion

  //#region signal
  $iconTheme = signal<string>('sun');

  $usuarioLogado = signal<IPessoa | null>(null);
  calendarPopoverAvailable = signal<boolean>(
    !!this.calendarPopoverComponentType
  );
  badgeCount = computed(() => this.calendarBadgeSignal());
  hasBadge = computed(() => this.badgeCount() > 0);
  $avatarImagemUrl = computed(() => {
    const usuario = this.$usuarioLogado();
    if (!usuario?.typeImage || !usuario?.imagemPerfil) {
      return null;
    }

    return `data:${usuario.typeImage};base64,${usuario.imagemPerfil}`;
  });
  isMobileMenuOpen = signal(false);
  $perfilLabel = computed(() => this.perfilLabelSignal?.() ?? null);
  tourReplayAvailable = !!this.tourReplay;
  helpVisible = this.helpEnabled === true;
  tourReplayKey = computed(
    () => this.tourReplay?.labelKey ?? 'PRODUCT_TOUR.REPLAY'
  );
  //#endregion

  @Input() featureSHeader: IFeatureSHeader = {
    logo: '',
    homeRoute: '/',
    badgeIsVisible: false,
    anchors: null,
  };

  @Output() onSignup = new EventEmitter<void>();

  @ViewChild('listaAgendamento') listaAgendamento?: NPopoverComponent;
  @ViewChild('calendarPopoverContainer', { read: ViewContainerRef })
  calendarPopoverContainer?: ViewContainerRef;

  private lastScrollY = 0;

  constructor() {
    effect(() => {
      this.$usuarioLogado.set(this.pessoaDataService.$userInfo());
    });
  }

  ngAfterViewInit(): void {
    this.renderCalendarPopover();
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    const currentScroll = window.scrollY;

    if (currentScroll > this.lastScrollY && currentScroll > 60) {
      // Rola pra baixo
      this.isHeaderHidden = true;
    } else {
      // Rola pra cima
      this.isHeaderHidden = false;
    }

    this.lastScrollY = currentScroll;
  }

  toggleTheme() {
    this.themeService.toggleDarkMode();

    if (this.themeService.$darkMode()) {
      this.$iconTheme.set('sun');
    } else {
      this.$iconTheme.set('moon');
    }
  }

  logout() {
    this.autDataService.logout();
  }
  editarPerfil() {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/minha-conta', this.$usuarioLogado()?.id])
    );
    window.open(url, '_blank');
  }

  tourReplayLabel(): string {
    const key = this.tourReplay?.labelKey ?? 'PRODUCT_TOUR.REPLAY';
    const value = this.translate.instant(key);
    return !value || value === key ? 'Ver tour de novo' : value;
  }

  replayTour(): void {
    this.tourReplay?.replay();
  }

  private renderCalendarPopover(): void {
    if (!this.calendarPopoverContainer) {
      return;
    }

    this.calendarPopoverContainer.clear();
    this.calendarPopoverComponentRef?.destroy();
    this.calendarItemClickedSub?.unsubscribe();

    if (!this.calendarPopoverComponentType) {
      return;
    }

    this.calendarPopoverComponentRef =
      this.calendarPopoverContainer.createComponent(
        this.calendarPopoverComponentType,
        {
          environmentInjector: this.environmentInjector,
        }
      );

    this.configureDynamicCalendarInstance(
      this.calendarPopoverComponentRef.instance
    );
  }

  private configureDynamicCalendarInstance(instance: unknown): void {
    if (!instance || typeof instance !== 'object') {
      return;
    }

    if ('numberRows' in instance) {
      (instance as { numberRows?: number }).numberRows = 2;
    }

    if ('showEmptyState' in instance) {
      (instance as { showEmptyState?: boolean }).showEmptyState = true;
    }

    const emitter = (
      instance as {
        itemClicado?: EventEmitter<void>;
      }
    ).itemClicado;

    if (emitter instanceof EventEmitter) {
      this.calendarItemClickedSub = emitter.subscribe(() => {
        this.listaAgendamento?.close();
        const route = this.calendarPopoverConfig?.calendarRoute;
        if (route && !this.router.url.startsWith(route)) {
          this.router.navigate([route]);
        }
      });
    }
  }

  navegarHome(): void {
    const route = this.featureSHeader.homeRoute || '/';
    this.closeMobileMenu();
    this.router.navigateByUrl(route);
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen.update((v) => !v);
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen.set(false);
  }

  ngOnDestroy(): void {
    this.calendarPopoverComponentRef?.destroy();
    this.calendarItemClickedSub?.unsubscribe();
  }
  irParaLogin() {
    this.router.navigate(['/login']);
  }
}
